import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Rubato header behaviour on top of Horizon's header component.
 *
 * 1. The drawer menu is used at every width, so the inline nav never appears.
 * 2. At the top of the page the header content colour follows the first section:
 *    white over a section flagged as a dark background, ink over a light one.
 *    The flag is set by the home sections as data-dark-background.
 * 3. Only one accordion is open at a time inside the menu panel.
 * 4. The Global-e announcement slot stays hidden until the script puts real text in it.
 */

const HEADER = 'header-component';

/** Horizon decides between the inline menu and the drawer on resize. Rubato always drawers. */
function forceDrawerMenu() {
  const header = document.querySelector(HEADER);
  if (!(header instanceof HTMLElement)) return;
  if (header.dataset.menuStyle !== 'drawer') header.dataset.menuStyle = 'drawer';
}

function firstSectionIsDark() {
  const main = document.querySelector('#MainContent') ?? document.querySelector('main');
  const first = main?.querySelector('.shopify-section');
  if (!first) return false;
  return first.matches('[data-dark-background="true"]') || Boolean(first.querySelector('[data-dark-background="true"]'));
}

function applyHeroColor() {
  const header = document.querySelector(HEADER);
  if (!(header instanceof HTMLElement) || !header.hasAttribute('transparent')) return;

  const dark = firstSectionIsDark();
  header.dataset.hero = dark ? 'dark' : 'light';
  header.style.setProperty('--color-transparent-text', dark ? '#FFFFFF' : '#212121');
  header.style.setProperty('--color-transparent-text-rgb', dark ? '255 255 255' : '33 33 33');
  header.style.setProperty('--color-transparent-text-contrast', dark ? '#212121' : '#F8F7F0');
}

/** One open accordion per group, as the brief asks. */
function closeSiblingAccordions(event) {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.open) return;
  if (!details.closest('.menu-drawer')) return;

  const group = details.parentElement?.parentElement;
  if (!group) return;

  for (const sibling of group.querySelectorAll('details[open]')) {
    if (sibling !== details && !sibling.contains(details)) sibling.open = false;
  }
}

/**
 * The newsletter stand-in collects nothing until Klaviyo's form id is set, so it never
 * submits. Round 50 gave it the rest of a field's manners: an empty or malformed address is
 * refused in our own type rather than left to the browser's bubble, and the message clears
 * as soon as the shopper types. A stand-in that takes an address and appears to keep it is
 * worse than no field at all.
 */
const NEWSLETTER_EMPTY = 'Enter your email address';
const NEWSLETTER_MALFORMED = 'That does not look like an email address';
const NEWSLETTER_THANKS = 'Thank you, you are on the list';
const NEWSLETTER_CONFIRM = 'Almost there. Check your inbox to confirm.';
const NEWSLETTER_FAILED = 'That did not go through. Try again in a moment.';
const NEWSLETTER_UNCONFIGURED = 'The newsletter is not connected yet';

/**
 * Round 61 part 20. The address goes to Klaviyo from here.
 *
 * Klaviyo's own embed used to take the whole column, and when it did not render the field
 * disappeared with it. The markup is the theme's now and only the subscription is Klaviyo's.
 *
 * Round 61 part 21: through manage.kmail-lists.com, not a.klaviyo.com/client/subscriptions.
 * The client endpoint is the current one in Klaviyo's documentation and it cannot be called
 * from a browser here: it answers the preflight with no Access-Control-Allow-Origin at all,
 * so the request never leaves. The older AJAX endpoint is form encoded, which is a simple
 * request with no preflight, and it answers 200 with a JSON body.
 *
 * That body is worth reading rather than just its status. success true with is_subscribed
 * false means Klaviyo has sent a confirmation email and the address is not on the list yet,
 * which is a different thing to tell someone than "you are on the list".
 */
async function subscribe(form, email) {
  const list = form.dataset.klaviyoList?.trim();
  if (!list) return { ok: false, reason: 'unconfigured' };
  /* 29 October: the source names the form, so a drop page's signups can be told apart from the
     footer's in Klaviyo. The footer's forms carry no source attribute and keep 'Footer'. */
  const source = form.dataset.klaviyoSource?.trim() || 'Footer';

  try {
    const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ g: list, email, $fields: '$source', $source: source }),
    });
    if (!response.ok) return { ok: false, reason: `http ${response.status}` };

    const body = await response.json().catch(() => null);
    if (body && body.success === false) {
      return { ok: false, reason: (body.errors && body.errors[0]) || 'refused' };
    }
    return { ok: true, confirmed: Boolean(body?.data?.is_subscribed) };
  } catch (error) {
    return { ok: false, reason: String(error?.message || error) };
  }
}

function setUpNewsletterPlaceholder() {
  for (const form of document.querySelectorAll('[data-rubato-newsletter]')) {
    if (form.dataset.rubatoWired === 'true') continue;
    form.dataset.rubatoWired = 'true';

    const input = form.querySelector('input[type="email"]');
    /* The message sits outside the form so the field's own rule is not broken by a line of
       text appearing under it. */
    const error = form.parentElement?.querySelector('[data-rubato-field-error]');

    const clear = () => {
      if (!error) return;
      error.hidden = true;
      error.textContent = '';
      input?.removeAttribute('aria-invalid');
    };

    const show = (text) => {
      if (!error) return;
      error.textContent = text;
      error.hidden = false;
      input?.setAttribute('aria-invalid', 'true');
    };

    input?.addEventListener('input', clear);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (input) {
        if (!input.value.trim()) {
          show(NEWSLETTER_EMPTY);
          input.focus();
          return;
        }
        if (!input.checkValidity()) {
          show(NEWSLETTER_MALFORMED);
          input.focus();
          return;
        }
      }

      clear();
      const submit = form.querySelector('button[type="submit"]');
      if (submit) submit.disabled = true;

      const result = await subscribe(form, input?.value.trim() ?? '');

      if (submit) submit.disabled = false;

      if (result.ok) {
        /* The answer lands in the same line the refusals use, so success and failure arrive
           in one place and in the theme's own type. The field empties: leaving an address
           sitting in it reads as though nothing happened.

           Round 62. And the field lets go. It kept focus through the submit, so an emptied
           box sat there wearing its focus ring with the confirmation underneath it, which
           reads as "try again" rather than "done". Blurred on success only: a refusal needs
           the cursor back in the field, a success does not.

           The line then clears itself the next time somebody puts the cursor in the field.
           It is already cleared on input; focus is the earlier moment and the one that says
           a second address is coming. */
        show(result.confirmed ? NEWSLETTER_THANKS : NEWSLETTER_CONFIRM);
        /* 29 October: a successful signup is published as rubato:newsletter_signup for the
           custom pixels under Customer events, which turn it into Meta's Lead. Only the
           source travels, never the address. Measurement never gets in the way of the form. */
        try {
          window.Shopify?.analytics?.publish?.('rubato:newsletter_signup', {
            source: form.dataset.klaviyoSource?.trim() || 'Footer',
          });
        } catch {
          /* nothing */
        }
        if (input) {
          input.value = '';
          input.blur();
          input.addEventListener('focus', clear, { once: true });
        }
        input?.removeAttribute('aria-invalid');
        return;
      }

      show(result.reason === 'unconfigured' ? NEWSLETTER_UNCONFIGURED : NEWSLETTER_FAILED);
    });
  }
}

/**
 * Round 62 part 3. Global-e's free shipping banner is taken out of the document.
 *
 * It is appended straight to <body> as div#FreeShippingBanner, first child, so it pushes the
 * page down by its own height the moment it lands. CSS hides it, but a rule cannot run before
 * the node exists: the banner is in the layout for the frame between insertion and paint, and
 * on the home page that frame is what made the header jump on scroll up.
 *
 * So it is removed rather than hidden. The observer watches the body's children alone, which
 * is where Global-e puts it, and it keeps watching, because Global-e re-inserts on navigation
 * within the session.
 */
const GLOBALE_BANNER = '#FreeShippingBanner, [id^="FreeShippingBanner"], [id*="GlobaleFreeShipping"]';

function removeGlobaleBanner(root = document) {
  for (const node of root.querySelectorAll?.(GLOBALE_BANNER) ?? []) node.remove();
}

function watchForGlobaleBanner() {
  removeGlobaleBanner();
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(GLOBALE_BANNER)) { node.remove(); continue; }
        removeGlobaleBanner(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchForGlobaleBanner);
} else {
  watchForGlobaleBanner();
}

/**
 * The Global-e free shipping banner is injected into an empty container under the header.
 *
 * CSS hides it with :empty, which stops applying the moment Global-e adds a node. A wrapper
 * with no text in it would then show as a band of empty ground, so mark the slot empty
 * whenever it holds no visible text and let CSS hide it on that instead.
 */
function markAnnouncementEmpty() {
  const slot = document.querySelector('[data-rubato-announcement]');
  if (!(slot instanceof HTMLElement)) return;

  const hasText = slot.textContent.trim().length > 0;
  const hasMedia = Boolean(slot.querySelector('img, svg, picture, video'));

  if (hasText || hasMedia) slot.removeAttribute('data-empty');
  else slot.setAttribute('data-empty', '');
}

function watchAnnouncement() {
  const slot = document.querySelector('[data-rubato-announcement]');
  if (!(slot instanceof HTMLElement)) return;

  markAnnouncementEmpty();

  // init() runs again on every shopify:section:load in the editor, so only observe once.
  if (slot.dataset.observed === 'true') return;
  slot.dataset.observed = 'true';

  // Global-e loads on its own schedule and can rewrite the banner after it lands.
  new MutationObserver(markAnnouncementEmpty).observe(slot, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

/**
 * The drawer's top row has to be exactly as tall as the header bar, so the x paints where the
 * hamburger painted and the drawer's Search where the word Search sat. The bar's height is
 * content driven, 64px on desktop and 60 on the phone at the moment, and the wordmark decides
 * it, so it is measured rather than assumed. Round 26, item 2.
 */
function measureHeaderHeight() {
  const header = document.querySelector(HEADER);
  if (!(header instanceof HTMLElement)) return;
  const columns = header.querySelector('.header__columns');
  const height = (columns ?? header).getBoundingClientRect().height;
  if (height > 0) document.documentElement.style.setProperty('--rubato-header-height', `${height}px`);
}

/**
 * The transparent header over a hero.
 *
 * Horizon drives the sticky state from the direction of the last scroll event, so a fast
 * flick, a momentum scroll that ends at the top, or a jump straight to the top can leave the
 * header in its active state with a ground background sitting over the hero. This reads the
 * scroll position itself on every change and puts the header back to its at-top state
 * whenever the page is at the top, whatever the last event said. Round 36, item 5.
 */
function scrollTopNow() {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight) {
    return wrapper.scrollTop;
  }
  return document.scrollingElement ? document.scrollingElement.scrollTop : window.scrollY;
}

function syncHeaderScrollState() {
  const header = document.querySelector(HEADER);
  if (!(header instanceof HTMLElement)) return;
  if (scrollTopNow() > 1) return;
  for (const node of [header, header.querySelector('.header')]) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.dataset.stickyState !== undefined && node.dataset.stickyState !== 'inactive') {
      node.dataset.stickyState = 'inactive';
    }
    if (node.dataset.scrollDirection !== undefined && node.dataset.scrollDirection !== 'none') {
      node.dataset.scrollDirection = 'none';
    }
  }
}

let headerSyncPending = false;
function queueHeaderSync() {
  if (headerSyncPending) return;
  headerSyncPending = true;
  requestAnimationFrame(() => {
    headerSyncPending = false;
    syncHeaderScrollState();
  });
}

function watchScrollForHeader() {
  const targets = new Set([window, document, document.querySelector('.page-wrapper')]);
  for (const t of targets) {
    if (!t) continue;
    t.removeEventListener('scroll', queueHeaderSync);
    t.addEventListener('scroll', queueHeaderSync, { passive: true });
  }
  window.addEventListener('scrollend', queueHeaderSync, { passive: true });
  window.addEventListener('pageshow', queueHeaderSync);
  window.addEventListener('resize', queueHeaderSync);
}

/**
 * Escape, and where the focus goes, on the menu drawer.
 *
 * The drawer is a details and summary pair, so it opens and closes by click but ignores
 * Escape, and opening it leaves the focus on the summary behind it. The brief asks for
 * keyboard operable overlays with Escape, and round 51's keyboard pass found neither. This
 * gives the drawer both: Escape closes it and hands the focus back to the control that
 * opened it, and opening it moves the focus to the first thing inside.
 */
function setUpMenuKeyboard() {
  const container = document.querySelector('#Details-menu-drawer-container, .menu-drawer-container');
  if (!(container instanceof HTMLDetailsElement) || container.dataset.rubatoKeyboard === 'true') return;
  container.dataset.rubatoKeyboard = 'true';

  const summary = container.querySelector('summary');

  container.addEventListener('toggle', () => {
    if (!container.open) return;

    /* The drawer animates in, and a control that is not painted yet cannot take the focus, so
       one attempt on the next frame lands about half the time. Try until it sticks or the
       drawer closes again. */
    const tryFocus = (attempt) => {
      if (!container.open) return;
      if (container.contains(document.activeElement) && document.activeElement !== summary) return;
      const first = container.querySelector(
        '.menu-drawer a[href], .menu-drawer button, .menu-drawer summary, .menu-drawer input'
      );
      if (first instanceof HTMLElement) first.focus();
      if (attempt < 4 && !container.contains(document.activeElement)) {
        setTimeout(() => tryFocus(attempt + 1), 120);
      }
    };
    requestAnimationFrame(() => tryFocus(0));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !container.open) return;
    event.preventDefault();
    container.open = false;
    if (summary instanceof HTMLElement) summary.focus();
  });
}

function init() {
  forceDrawerMenu();
  setUpMenuKeyboard();
  applyHeroColor();
  setUpNewsletterPlaceholder();
  watchAnnouncement();
  measureHeaderHeight();
  watchScrollForHeader();
  syncHeaderScrollState();
}

document.addEventListener('toggle', closeSiblingAccordions, true);
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:reorder', applyHeroColor);
window.addEventListener('resize', forceDrawerMenu);
window.addEventListener('resize', measureHeaderHeight);
document.fonts?.ready.then(measureHeaderHeight);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Horizon sets data-menu-style from its own resize and load handlers, so keep watch.
const header = document.querySelector(HEADER);
if (header) {
  new MutationObserver(forceDrawerMenu).observe(header, {
    attributes: true,
    attributeFilter: ['data-menu-style'],
  });
}

/**
 * Round 61 part 15: a page restored from bfcache never comes back with a drawer open.
 *
 * Swiping back from a collection to the page the menu was opened on used to flash the drawer
 * open, close it, and leave the page unscrollable. The browser restores the document exactly
 * as it was left, which includes an open <dialog>, an open <details> drawer and the
 * scroll-lock attribute on <html>. The theme's own close handlers had already run before the
 * page was frozen, so nothing was left to undo it.
 *
 * Everything that can hold the page is closed here, on a restore and only on a restore, and
 * the lock is cleared last so it cannot be re-applied by something closing after it. No drawer
 * in the theme pushes a history entry, so there is nothing to unwind: the cart and menu
 * drawers are <details> and <dialog> elements, and the only pushState calls belong to filters
 * and pagination.
 */
/**
 * Round 61 part 26: the menu drawer is the exception.
 *
 * Closing everything was right for the cart, the size guide and the notify sheets, and wrong
 * for the menu. Opening the menu, following a link into a collection and pressing Back showed
 * the menu for a moment and then dropped it, which is the same flash the part 15 fix was
 * written to remove, only now caused by the fix. Back from a menu link should land on the menu
 * it was opened from: the customer is still choosing.
 *
 * So the menu is left as the restore found it, and the scroll lock follows the menu rather
 * than being cleared on principle. The lock is owned by utilities.js, which keeps a set of
 * owners and a MutationObserver that puts the attribute back whenever the set is not empty.
 * Setting or clearing the attribute by hand here would be undone by the next DOM change, so
 * the owner is added and removed through the same API the drawers use and the attribute is
 * left to follow.
 */
const MENU_DRAWER = '#Details-menu-drawer-container, .menu-drawer-container';

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  const menu = document.querySelector(MENU_DRAWER);
  const menuOpen = menu instanceof HTMLDetailsElement && menu.open;

  for (const dialog of document.querySelectorAll('dialog[open]')) {
    try {
      dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }
  }

  for (const drawer of document.querySelectorAll('details[open]')) {
    /* Accordions on a product page are content, not overlays, and are left as they were. */
    if (drawer.closest('.product-details, .rubato-size-guides, .accordion')) continue;
    if (menu && (drawer === menu || menu.contains(drawer))) continue;
    drawer.open = false;
  }

  for (const expanded of document.querySelectorAll('[aria-expanded="true"]')) {
    if (menuOpen && menu.contains(expanded)) continue;
    expanded.setAttribute('aria-expanded', 'false');
  }

  document.body.style.removeProperty('overflow');

  if (menuOpen) {
    /* Re-registered rather than assumed. bfcache keeps the heap, so the owner is usually still
       in the set, and adding it twice is a no-op on a Set. What it guards against is the case
       where something ran on the way out and took it back off. */
    lockScroll(menu);
  } else {
    if (menu) unlockScroll(menu);
    document.documentElement.removeAttribute('scroll-lock');
  }
});
